import { useState, useEffect, useRef } from 'react';

import { Chess } from "chess.js";

export function useStockfishAnalysis() {
  const workerRef      = useRef(null);

  // --- runtime refs seen by the worker handler ---
  const modeRef        = useRef("idle");          // 'idle' | 'analyse' | 'refute'
  const stageRef       = useRef("idle");          // 'idle' | 'best' | 'refute'
  const fenRef         = useRef("");
  const badMoveRef     = useRef("");
  const depthRef       = useRef(19);
  const bestInfoRef    = useRef(null);
  const wrongInfoRef   = useRef(null);

  // --- React state for UI ---
  const [mode, setMode] = useState("idle");
  const [infoList, setInfoList] = useState([]);
  const [refuteResult, setRefuteResult] = useState({
    best: null, wrong: null, ponder: null,
  });

  // keep the ref in sync with state (so worker sees latest)
  useEffect(() => { modeRef.current = mode; }, [mode]);

  const wasmPath = "/stockfish-17-lite-single.js";

  useEffect(() => {
    const w = new Worker(wasmPath);
    w.postMessage("uci");
    w.postMessage("isready");

    w.onmessage = (e) => {
      const text = (e.data || "").toString().trim();
      // console.log(`stage: ${stageRef.current}, mode: ${modeRef.current}, text: ${text}`);
      const currentMode = modeRef.current;

      if (currentMode === "analyse") {
        if (text.startsWith("info")) {
          const info = parseUCIInfo(text);
          setInfoList((prev) => [...prev, info]);
        }
        return;
      }

      if (currentMode === "refute") {
        // Stage: best line (unconstrained)
        if (stageRef.current === "best" && text.startsWith("info") && text.includes(" pv ")) {
          bestInfoRef.current = parseUCIInfo(text);
        }

        if (stageRef.current === "best" && text.startsWith("bestmove")) {
          stageRef.current = "refute";
          // w.postMessage("ucinewgame");
          // console.log("REFUTE phase:",
          //   fenRef.current,
          //   badMoveRef.current,
          // );
          // w.postMessage(`position fen ${fenRef.current}`);
          w.postMessage(`go depth ${depthRef.current} searchmoves ${badMoveRef.current}`);
          return;
        }

        // Stage: refutation of bad move (searchmoves)
        if (stageRef.current === "refute" && text.startsWith("info") && text.includes(" pv ")) {
          wrongInfoRef.current = parseUCIInfo(text);
        }
        if (stageRef.current === "refute" && text.startsWith("bestmove")) {
          const tokens = text.split(/\s+/);
          const ponder = tokens[2] === "ponder" ? tokens[3] : null;
          setRefuteResult({
            best:   bestInfoRef.current,
            wrong:  wrongInfoRef.current,
            ponder,
          });
          stageRef.current = "idle";
          setMode("idle");
        }
      }
    };

    workerRef.current = w;
    return () => w.terminate();
  }, [wasmPath]);

  // ---------- public APIs ----------
  const analyse = (fen, depth = 19, multiPV = 3) => {
    setMode("analyse");
    setInfoList([]);
    depthRef.current = depth;

    const w = workerRef.current;
    w.postMessage("ucinewgame");
    w.postMessage(`setoption name MultiPV value ${multiPV}`);
    w.postMessage(`position fen ${fen}`);
    w.postMessage(`go depth ${depth}`);
  };

  const refute = (fen, badMove, depth = 19) => {
    const wrongMoveUCI = sanToUci(fen, badMove);
    console.log(`Refuting ${wrongMoveUCI} in ${fen}`);
    setMode("refute");
    bestInfoRef.current  = null;
    wrongInfoRef.current = null;
    setRefuteResult({ best: null, wrong: null, ponder: null });

    fenRef.current     = fen;
    badMoveRef.current = wrongMoveUCI;
    depthRef.current   = depth;

    stageRef.current = "best";
    const w = workerRef.current;
    w.postMessage("ucinewgame");
    w.postMessage(`position fen ${fen}`);
    w.postMessage(`go depth ${depth}`);
  };

  return { mode, infoList, ...refuteResult, analyse, refute };
}

// same as before
export function parseUCIInfo(line) {
  const tokens = line.split(" ");
  const info = {};
  for (let i = 1; i < tokens.length; i++) {
    switch (tokens[i]) {
      case "depth":    info.depth = +tokens[++i]; break;
      case "seldepth": info.seldepth = +tokens[++i]; break;
      case "multipv":  info.multipv = +tokens[++i]; break;
      case "score": {
        const kind = tokens[++i];
        const val  = tokens[++i];
        info.score = kind === "cp" ? Number(val) / 100 : `Mate in ${val}`;
        break;
      }
      case "nodes": info.nodes = +tokens[++i]; break;
      case "nps":   info.nps = +tokens[++i]; break;
      case "pv":
        info.pv = tokens.slice(i + 1);
        i = tokens.length;
        break;
      default:
        break;
    }
  }
  return info;
}

// SAN → UCI (e.g. "Bb7" -> "d5b7"; "exd8=Q+" -> "e7d8q")
function sanToUci(fen, san) {
  const game = new Chess(fen);
  // const cleaned = normalizeSAN(san);
  // sloppy allows a bit of flexibility with SAN inputs
  const move = game.move(san, { sloppy: true });
  if (!move) {
    throw new Error(`Illegal SAN "${san}" for FEN: ${fen}`);
  }
  const promo = move.promotion ? move.promotion.toLowerCase() : "";
  return `${move.from}${move.to}${promo}`;
}
